import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Scissors, Crop, Move, SlidersHorizontal, Plus, X } from 'lucide-react';
import {
  DEFAULT_TRANSITION_OUT,
  defaultDurationForTransition,
  normalizeTransitionOut,
  transitionsByCategory,
  TRANSITION_CATEGORIES,
} from '@/lib/videoTransitions';
import {
  clampFilterIntensity,
  defaultIntensityForFilter,
  filtersByCategory,
  FILTER_CATEGORIES,
  getFilterById,
  normalizeClipFilters,
  normalizeFilterId,
} from '@/lib/videoFilters';
import { ClipFilterMediaPreview } from '@/components/ClipFilterMediaPreview';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  motionPresetsByCategory,
  motionPreviewStyleFromPreset,
  normalizeMotionPreset,
} from '@/lib/videoMotionPresets';

import { TwinneeLogo } from '@/components/TwinneeLogo';
const formatTime = (s) => {
  const m = Math.floor(s / 60);
  const sec = (s % 60).toFixed(1);
  return `${String(m).padStart(2, '0')}:${String(sec).padStart(4, '0')}`;
};

export function VideoClipEditor({ isOpen, onClose, clip, onSave, onClipPatch }) {
  const duration = clip?.duration || 5;
  const [activeTab, setActiveTab] = useState('trim');
  const [trimStart, setTrimStart] = useState(clip?.trimStart || 0);
  const [trimEnd, setTrimEnd] = useState(clip?.trimEnd || duration);
  const [motionPreset, setMotionPreset] = useState(clip?.motionPreset || 'none');
  const [motionStrengthPct, setMotionStrengthPct] = useState(
    Math.round((Number(clip?.motionStrength) || 0.12) * 100),
  );
  const [transitionOut, setTransitionOut] = useState(
    normalizeTransitionOut(clip?.transitionOut || DEFAULT_TRANSITION_OUT),
  );
  const [transitionDuration, setTransitionDuration] = useState(
    Number(clip?.transitionDuration) || defaultDurationForTransition(clip?.transitionOut),
  );
  const transitionsGrouped = useMemo(() => transitionsByCategory(), []);
  const filtersGrouped = useMemo(() => filtersByCategory(), []);
  const motionGrouped = useMemo(() => motionPresetsByCategory(), []);
  const [videoFilters, setVideoFilters] = useState(() => normalizeClipFilters(clip?.videoFilters));
  const [addFilterId, setAddFilterId] = useState('');
  const motionStrength = useMemo(() => {
    const pct = Math.max(1, Math.min(35, Number(motionStrengthPct) || 12));
    return pct / 100;
  }, [motionStrengthPct]);
  const lastPatchKeyRef = useRef('');

  const buildClipDraft = useCallback(() => ({
    ...clip,
    trimStart,
    trimEnd,
    playbackDuration: trimEnd - trimStart,
    motionPreset: motionPreset || 'none',
    motionStrength,
    transitionOut: normalizeTransitionOut(transitionOut),
    transitionDuration: transitionOut === 'cut'
      ? 0
      : Math.max(0.1, Math.min(2, Number(transitionDuration) || 0.5)),
    videoFilters: normalizeClipFilters(videoFilters),
  }), [
    clip,
    trimStart,
    trimEnd,
    motionPreset,
    motionStrength,
    transitionOut,
    transitionDuration,
    videoFilters,
  ]);

  useEffect(() => {
    lastPatchKeyRef.current = '';
  }, [clip?.id]);

  useEffect(() => {
    if (!onClipPatch || !clip?.id) return;
    const draft = buildClipDraft();
    const key = JSON.stringify({
      id: draft.id,
      vf: draft.videoFilters,
      mp: draft.motionPreset,
      ms: draft.motionStrength,
      to: draft.transitionOut,
      td: draft.transitionDuration,
      ts: draft.trimStart,
      te: draft.trimEnd,
    });
    if (key === lastPatchKeyRef.current) return;
    lastPatchKeyRef.current = key;
    onClipPatch(draft);
  }, [onClipPatch, clip?.id, buildClipDraft]);

  const handleSave = () => {
    onSave(buildClipDraft());
    onClose();
  };

  const handleReset = () => {
    setTrimStart(0);
    setTrimEnd(duration);
    setMotionPreset('none');
    setMotionStrengthPct(12);
    setTransitionOut(DEFAULT_TRANSITION_OUT);
    setTransitionDuration(0);
    setVideoFilters([]);
    setAddFilterId('');
  };

  const handleAddFilter = () => {
    const id = normalizeFilterId(addFilterId);
    if (!id || videoFilters.some((f) => f.id === id)) return;
    setVideoFilters((prev) => [...prev, { id, intensity: defaultIntensityForFilter(id) }]);
    setAddFilterId('');
  };

  const updateFilterIntensity = (id, intensity) => {
    setVideoFilters((prev) => prev.map((f) => (
      f.id === id ? { ...f, intensity: clampFilterIntensity(intensity, id) } : f
    )));
  };

  const removeFilter = (id) => {
    setVideoFilters((prev) => prev.filter((f) => f.id !== id));
  };

  /** Filters tab preview: show selected dropdown filter before "+" is clicked. */
  const filtersTabPreviewFilters = useMemo(() => {
    const pendingId = normalizeFilterId(addFilterId);
    if (pendingId && !videoFilters.some((f) => f.id === pendingId)) {
      return [{ id: pendingId, intensity: defaultIntensityForFilter(pendingId) }];
    }
    return normalizeClipFilters(videoFilters);
  }, [addFilterId, videoFilters]);

  const pendingFilterMeta = useMemo(() => {
    const pendingId = normalizeFilterId(addFilterId);
    if (!pendingId || videoFilters.some((f) => f.id === pendingId)) return null;
    return getFilterById(pendingId);
  }, [addFilterId, videoFilters]);

  if (!clip) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" style={{ background: 'var(--bg-primary)', border: '1px solid var(--glass-border)' }}>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <Scissors className="w-4 h-4" style={{ color: 'var(--primary)' }} />
            Edit Clip: {clip.name}
          </DialogTitle>
        </DialogHeader>

        {/* Video preview — filters update live as you edit */}
        <div className="relative aspect-video rounded-xl overflow-hidden" style={{ background: 'var(--bg-tertiary)' }}>
          {(clip.videoUrl || clip.imageUrl) ? (
            <ClipFilterMediaPreview
              clip={clip}
              filters={videoFilters}
              className="absolute inset-0 w-full h-full"
              videoProps={{ controls: true }}
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: 'var(--text-tertiary)' }}>No preview</div>
          )}
          <div className="absolute bottom-2 right-2 text-xs px-2 py-1 rounded-full z-20 pointer-events-none"
            style={{ background: '#0008', color: '#fff' }}>
            {formatTime(trimEnd - trimStart)} / {formatTime(duration)}
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 rounded-xl p-1" style={{ background: 'var(--bg-tertiary)' }}>
          {[
            { id: 'trim', icon: Scissors, label: 'Trim' },
            { id: 'motion', icon: Move, label: 'Motion' },
            { id: 'transition', icon: TwinneeLogo, label: 'Transition' },
            { id: 'filters', icon: SlidersHorizontal, label: 'Filters' },
            { id: 'info', icon: Crop, label: 'Info' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className="flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-sm font-medium transition-all"
              style={{
                background: activeTab === t.id ? 'var(--primary)' : 'transparent',
                color: activeTab === t.id ? 'white' : 'var(--text-secondary)',
              }}>
              <t.icon className="w-3.5 h-3.5" /> {t.label}
            </button>
          ))}
        </div>

        {/* Tab content */}
        {activeTab === 'trim' && (
          <div className="space-y-5">
            <div>
              <div className="flex justify-between text-xs mb-2" style={{ color: 'var(--text-secondary)' }}>
                <span>Start: {formatTime(trimStart)}</span>
                <span>End: {formatTime(trimEnd)}</span>
                <span>Duration: {formatTime(trimEnd - trimStart)}</span>
              </div>
              {/* Visual timeline */}
              <div className="relative h-10 rounded-lg overflow-hidden mb-3" style={{ background: 'var(--bg-tertiary)' }}>
                <div className="absolute inset-y-0 rounded-lg"
                  style={{
                    left: `${(trimStart / duration) * 100}%`,
                    width: `${((trimEnd - trimStart) / duration) * 100}%`,
                    background: 'var(--primary)',
                    opacity: 0.7
                  }} />
                <div className="absolute inset-0 flex items-center justify-center text-xs font-medium"
                  style={{ color: 'white' }}>Selected clip region</div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Trim Start</label>
                  <Slider value={[trimStart]} min={0} max={Math.max(0, trimEnd - 0.5)} step={0.1}
                    onValueChange={([v]) => setTrimStart(Math.min(v, trimEnd - 0.5))} />
                </div>
                <div>
                  <label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>Trim End</label>
                  <Slider value={[trimEnd]} min={Math.min(trimStart + 0.5, duration)} max={duration} step={0.1}
                    onValueChange={([v]) => setTrimEnd(Math.max(v, trimStart + 0.5))} />
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'motion' && (
          <div className="space-y-4">
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Add per-clip motion (zoom/pan). Applied during preview and export.
            </div>
            <div className="space-y-2">
              <label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                Preset
              </label>
              <Select value={normalizeMotionPreset(motionPreset)} onValueChange={setMotionPreset}>
                <SelectTrigger className="h-9 rounded-lg text-sm"
                  style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  <SelectItem value="none">None</SelectItem>
                  {motionGrouped.map((group) => (
                    <SelectGroup key={group.id}>
                      <SelectLabel className="text-xs">{group.label}</SelectLabel>
                      {group.presets.map((p) => (
                        <SelectItem key={p.id} value={p.id}>{p.label}</SelectItem>
                      ))}
                    </SelectGroup>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-xs" style={{ color: 'var(--text-secondary)' }}>
                <span>Strength</span>
                <span className="tabular-nums">{motionStrengthPct}%</span>
              </div>
              <Slider
                value={[motionStrengthPct]}
                min={1}
                max={35}
                step={1}
                onValueChange={([v]) => setMotionStrengthPct(v)}
              />
            </div>
            <div className="rounded-xl overflow-hidden border"
              style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}>
              <div className="relative aspect-video overflow-hidden">
                {clip.imageUrl ? (
                  <img
                    src={clip.imageUrl}
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover"
                    style={motionPreviewStyleFromPreset(motionPreset, 0.45, motionStrength)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-xs" style={{ color: 'var(--text-tertiary)' }}>
                    Motion preview is available for image clips.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'transition' && (
          <div className="space-y-4">
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Choose how this clip transitions into the next clip on the same video track.
              Cuts are instant; other effects blend over the selected duration.
            </div>
            <div className="space-y-2">
              <label className="text-xs mb-1 block" style={{ color: 'var(--text-secondary)' }}>
                Transition to next clip
              </label>
              <Select
                value={transitionOut}
                onValueChange={(v) => {
                  const next = normalizeTransitionOut(v);
                  setTransitionOut(next);
                  if (next !== 'cut') {
                    setTransitionDuration(defaultDurationForTransition(next));
                  } else {
                    setTransitionDuration(0);
                  }
                }}
              >
                <SelectTrigger className="h-9 rounded-lg text-sm"
                  style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="max-h-[min(60vh,420px)]">
                  {TRANSITION_CATEGORIES.map((cat) => {
                    const items = transitionsGrouped[cat.id] || [];
                    if (!items.length) return null;
                    return (
                      <div key={cat.id}>
                        <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{ color: 'var(--text-tertiary)' }}>
                          {cat.label}
                        </div>
                        {items.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            <span className="block">{t.label}</span>
                            {t.useCase ? (
                              <span className="block text-[10px] opacity-70">{t.useCase}</span>
                            ) : null}
                          </SelectItem>
                        ))}
                      </div>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            {transitionOut !== 'cut' ? (
              <div className="space-y-2">
                <div className="flex justify-between text-xs" style={{ color: 'var(--text-secondary)' }}>
                  <span>Duration</span>
                  <span className="tabular-nums">{Number(transitionDuration).toFixed(1)}s</span>
                </div>
                <Slider
                  value={[Math.round(Number(transitionDuration) * 10) || 5]}
                  min={1}
                  max={20}
                  step={1}
                  onValueChange={([v]) => setTransitionDuration(v / 10)}
                />
              </div>
            ) : null}
          </div>
        )}

        {activeTab === 'filters' && (
          <div className="space-y-4">
            <div className="text-xs" style={{ color: 'var(--text-secondary)' }}>
              Pick overlays, theme tones, or special effects. Stack a few for a custom look — all bake into export.
            </div>
            {(clip?.videoUrl || clip?.imageUrl) ? (
              <div className="rounded-xl overflow-hidden border"
                style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-tertiary)' }}>
                {pendingFilterMeta ? (
                  <div className="px-3 py-1.5 text-[10px] font-medium border-b"
                    style={{ borderColor: 'var(--glass-border)', color: 'var(--primary)' }}>
                    Previewing: {pendingFilterMeta.label} — click + to add
                  </div>
                ) : null}
                <ClipFilterMediaPreview
                  clip={clip}
                  filters={filtersTabPreviewFilters}
                  className="aspect-video w-full bg-black/40"
                  videoProps={{ muted: true, playsInline: true, loop: true, autoPlay: true }}
                />
              </div>
            ) : null}
            {videoFilters.length > 0 ? (
              <div className="space-y-3 max-h-52 overflow-y-auto pr-1">
                {videoFilters.map((f) => {
                  const meta = getFilterById(f.id);
                  return (
                    <div key={f.id} className="rounded-xl p-3 space-y-2"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--glass-border)' }}>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>
                          {meta?.label || f.id}
                        </span>
                        <button type="button" className="p-1 rounded hover:bg-white/10"
                          onClick={() => removeFilter(f.id)}
                          aria-label={`Remove ${meta?.label || f.id}`}>
                          <X className="w-3.5 h-3.5" style={{ color: 'var(--text-secondary)' }} />
                        </button>
                      </div>
                      {meta?.useCase ? (
                        <p className="text-[10px]" style={{ color: 'var(--text-tertiary)' }}>{meta.useCase}</p>
                      ) : null}
                      <div className="flex justify-between text-[10px]" style={{ color: 'var(--text-secondary)' }}>
                        <span>Intensity</span>
                        <span className="tabular-nums">{f.intensity}%</span>
                      </div>
                      <Slider
                        value={[f.intensity]}
                        min={0}
                        max={100}
                        step={1}
                        onValueChange={([v]) => updateFilterIntensity(f.id, v)}
                      />
                    </div>
                  );
                })}
              </div>
            ) : pendingFilterMeta ? (
              <div className="text-xs rounded-lg px-3 py-4 text-center"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}>
                Previewing {pendingFilterMeta.label} above. Click + to add it to this clip.
              </div>
            ) : (
              <div className="text-xs rounded-lg px-3 py-4 text-center"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-tertiary)' }}>
                No filters applied. Pick one below to preview, then click + to add.
              </div>
            )}
            <div className="flex gap-2">
              <Select value={addFilterId || undefined} onValueChange={setAddFilterId}>
                <SelectTrigger className="h-9 rounded-lg text-sm flex-1"
                  style={{ borderColor: 'var(--glass-border)', background: 'var(--bg-primary)' }}>
                  <SelectValue placeholder="Add filter…" />
                </SelectTrigger>
                <SelectContent className="max-h-[min(50vh,360px)]">
                  {FILTER_CATEGORIES.map((cat) => {
                    const items = filtersGrouped[cat.id] || [];
                    if (!items.length) return null;
                    return (
                      <div key={cat.id}>
                        <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wide"
                          style={{ color: 'var(--text-tertiary)' }}>
                          {cat.label}
                        </div>
                        {items.map((flt) => (
                          <SelectItem
                            key={flt.id}
                            value={flt.id}
                            disabled={videoFilters.some((vf) => vf.id === flt.id)}
                          >
                            {flt.label}
                          </SelectItem>
                        ))}
                      </div>
                    );
                  })}
                </SelectContent>
              </Select>
              <Button type="button" onClick={handleAddFilter} disabled={!addFilterId}
                className="rounded-xl shrink-0" style={{ background: 'var(--primary)', color: 'white' }}>
                <Plus className="w-4 h-4" />
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'info' && (
          <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
            <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--glass-border)' }}>
              <span>Scene</span><span className="font-medium" style={{ color: 'var(--text-primary)' }}>{clip.name}</span>
            </div>
            <div className="flex justify-between py-2 border-b" style={{ borderColor: 'var(--glass-border)' }}>
              <span>Original Duration</span><span className="font-medium" style={{ color: 'var(--text-primary)' }}>{formatTime(duration)}</span>
            </div>
            <div className="flex justify-between py-2">
              <span>Clip Duration</span><span className="font-medium" style={{ color: 'var(--primary)' }}>{formatTime(trimEnd - trimStart)}</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 pt-1">
          <Button variant="outline" onClick={handleReset} className="rounded-xl text-xs"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}>
            Reset
          </Button>
          <Button variant="outline" onClick={onClose} className="rounded-xl text-xs"
            style={{ borderColor: 'var(--glass-border)', color: 'var(--text-secondary)' }}>
            Cancel
          </Button>
          <Button onClick={handleSave} className="flex-1 rounded-xl text-xs"
            style={{ background: 'var(--primary)', color: 'white' }}>
            Apply Changes
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
